import React, { useEffect, useState } from 'react';
import { notify } from '../utils/alertBus';

const DiscountModal = ({
    isOpen,
    onClose,
    variety,
    discounts,
    varietyPrice,
    handleDiscountChange,
    closeEverything,
}) => {
    if (!isOpen || !variety || !varietyPrice) return null;
    if (varietyPrice.price === null || varietyPrice.price === undefined) return null;

    const [newPriceRaw, setNewPriceRaw] = useState('0');
    const [inputMode, setInputMode] = useState('price'); // 'price' or 'percent'

    useEffect(() => {
        const index = variety.VarietyPrices.indexOf(varietyPrice);
        const existingDiscount = discounts[variety.id]?.[index] || 0;
        const startingPrice = varietyPrice.price - existingDiscount;
        setNewPriceRaw(String(startingPrice > 0 && startingPrice !== varietyPrice.price ? startingPrice : '0'));
        setInputMode('price');
    }, [discounts, variety, varietyPrice]);

    const handleInputChange = (value) => {
        value = value.toString() || '';
        let cleaned = value.replace(/[^\d]/g, '');
        cleaned = cleaned.replace(/^0+(?!$)/, '') || '0';
        setNewPriceRaw(cleaned);
    };

    const handlePadInput = (key) => {
        let value = newPriceRaw;

        if (key === 'C') {
            value = '0';
        } else if (key === '←') {
            value = value.slice(0, -1) || '0';
        } else if (/\d/.test(key)) {
            value = value === '0' ? key : value + key;
        } else if (/\d/.test(key) || key === '00') {
            value = value === '0' ? key : value + key;
        }

        handleInputChange(value);
    };

    const applyDiscount = () => {
        const index = variety.VarietyPrices.indexOf(varietyPrice);
        const original = varietyPrice.price;

        const parsed = parseInt(newPriceRaw || '0', 10);

        if (inputMode === 'percent') {
            if (isNaN(parsed) || parsed <= 0 || parsed >= 100) {
                notify("Ingresa un porcentaje válido (1-99).");
                return;
            }
            const discount = Math.floor(original * (parsed / 100));
            handleDiscountChange(variety.id, index, discount);
            onClose();
            closeEverything();
            return;
        }

        const newPrice = parsed;

        if (newPrice === 0 || newPrice === original) {
            handleDiscountChange(variety.id, index, 0);
            onClose();
            closeEverything();
            return;
        }

        if (newPrice > original) {
            notify("El nuevo precio no puede ser mayor al original.");
            return;
        }

        const discount = original - newPrice;
        handleDiscountChange(variety.id, index, discount);
        onClose();
        closeEverything();
    };

    const price = varietyPrice.price;
    const rawNumber = parseInt(newPriceRaw || '0', 10);
    const percentValue = Math.min(Math.max(rawNumber || 0, 0), 100);
    const discount =
        inputMode === 'percent'
            ? Math.floor(price * (percentValue / 100))
            : Math.max(price - (isNaN(rawNumber) ? price : rawNumber), 0);
    const finalPrice = Math.max(price - discount, 0);
    const discountPercent = price > 0 ? Math.round((discount / price) * 100) : 0;

    return (
        <div
            className="fixed inset-0 bg-black/50 z-[1000] flex justify-center items-start overflow-y-auto px-4 py-6"
            onClick={onClose}
        >
            <div
                className="bg-white w-full max-w-md rounded-2xl shadow-2xl border border-gray-100 overflow-hidden max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
                    <div>
                        <p className="text-xs uppercase tracking-wide text-gray-500">Descuento</p>
                        <h2 className="text-lg font-semibold text-gray-900">Aplicar descuento</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-600 hover:text-gray-900 bg-white border border-gray-200 rounded-full w-9 h-9 flex items-center justify-center"
                    >
                        ✕
                    </button>
                </div>

                <div className="p-6 space-y-5">
                    <div className="flex items-center gap-3">
                        <img
                            src={varietyPrice.priceImage || variety.varietyImage || 'https://placehold.co/200'}
                            alt={variety.varietyName}
                            className="w-16 h-16 object-cover rounded-lg shadow"
                        />
                        <div>
                            <div className="text-sm font-semibold text-gray-900">{variety.varietyName}</div>
                            <div className="text-xs text-gray-500">{varietyPrice.metricUnit}</div>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                            <div className="text-xs text-gray-500">Precio original</div>
                            <div className="text-base font-semibold text-gray-900">${price}</div>
                        </div>
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                            <div className="text-xs text-gray-500">
                                {inputMode === 'percent' ? 'Porcentaje' : 'Nuevo precio'}
                            </div>
                            <div className="text-base font-semibold text-gray-900">
                                {inputMode === 'percent' ? `${percentValue}%` : `$${finalPrice}`}
                            </div>
                        </div>
                    </div>

                    <div className="text-sm text-green-700 bg-green-50 border border-green-100 rounded-lg p-3">
                        Descuento aplicado: <strong>${discount}</strong>
                        {discountPercent > 0 && <> ({discountPercent}%)</>}
                    </div>

                    <div className="relative">
                        <input
                            type="text"
                            inputMode="numeric"
                            value={newPriceRaw}
                            onChange={(e) => handleInputChange(e.target.value)}
                            className="text-center text-xl border border-gray-300 p-3 w-full rounded-lg pr-10"
                        />
                        <span className="absolute inset-y-0 right-3 flex items-center text-gray-500">
                            {inputMode === 'percent' ? '%' : '$'}
                        </span>
                    </div>

                    <div className="grid grid-cols-3 gap-2">
                        {[
                            1,
                            2,
                            3,
                            4,
                            5,
                            6,
                            7,
                            8,
                            9,
                            'C',
                            0,
                            '00',
                            '←',
                        ].map((key, idx) => (
                            <button
                                key={idx}
                                onClick={() => {
                                    if (key === '%') {
                                        setInputMode('percent');
                                    } else {
                                        handlePadInput(key);
                                    }
                                }}
                                className="py-3 text-lg rounded-lg bg-gray-100 hover:bg-gray-200"
                            >
                                {key}
                            </button>
                        ))}

                        <button
                            onClick={applyDiscount}
                            className="col-span-3 py-3 bg-blue-600 text-white text-lg rounded-lg hover:bg-blue-700"
                        >
                            Aplicar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DiscountModal;
